import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';

/**
 * @typedef {Object} ITabItem
 * @property {string} id - Identificador único del tab
 * @property {React.ReactNode} content - Contenido a mostrar cuando el tab está activo
 * @property {string|React.ReactNode} label - Etiqueta a mostrar en el tab
 * @property {React.ReactNode} [icon] - Icono opcional para el tab
 */

/**
 * @typedef {Object} TabsComponentProps
 * @property {ITabItem[]} tabList - Lista de tabs a mostrar
 * @property {string} [activeTab] - ID del tab activo inicial
 * @property {'default'|'pills'|'underline'|'bordered'} [variant='default'] - Variante visual de los tabs
 * @property {'sm'|'md'|'lg'} [size='md'] - Tamaño del padding y texto
 * @property {string} [className=''] - Clases CSS adicionales para el contenedor principal
 * @property {string} [tabsWrapperClassName=''] - Clases CSS adicionales para el wrapper de tabs
 * @property {string} [tabsContainerClassName=''] - Clases CSS adicionales para el contenedor de tabs
 * @property {string} [contentClassName=''] - Clases CSS adicionales para el área de contenido
 * @property {string} [activeTabClassName=''] - Clases CSS adicionales para el tab activo
 * @property {string} [inactiveTabClassName=''] - Clases CSS adicionales para tabs inactivos
 * @property {function(string): void} [onTabChange] - Callback ejecutado cuando cambia el tab activo
 * @property {boolean} [keepMounted=false] - Mantener montado el contenido de todos los tabs (ocultar inactivos)
 */

/**
 * Componente de pestañas flexible y personalizable que sigue los principios de Atomic Design.
 * Es un organismo que puede ser utilizado en diferentes contextos para organizar contenido en pestañas navegables.
 * 
 * @component
 * @example
 * // Uso básico
 * const tabList = [
 *   { id: 'tab1', label: 'Tab 1', content: <div>Contenido 1</div> },
 *   { id: 'tab2', label: 'Tab 2', content: <div>Contenido 2</div> }
 * ];
 * 
 * <TabsComponentMC tabList={tabList} />
 * 
 * @example
 * // Con variantes y personalización
 * <TabsComponentMC 
 *   tabList={tabList}
 *   variant="pills"
 *   size="lg"
 *   onTabChange={(tabId) => console.log('Tab changed:', tabId)}
 *   activeTabClassName="tw-bg-blue-500"
 * />
 * 
 * @param {TabsComponentProps} props - Props del componente
 * @returns {React.ReactElement|null} Componente de tabs o null si no hay tab activo válido
 */
const TabsComponentMC = memo(({ 
  tabList, 
  activeTab,
  variant = 'default',
  size = 'md',
  className = '',
  tabsWrapperClassName = '',
  tabsContainerClassName = '',
  contentClassName = '',
  activeTabClassName = '',
  inactiveTabClassName = '',
  onTabChange,
  keepMounted = false
}) => {
  // Estado memoizado para el tab activo
  const [activeTabId, setActiveTabId] = useState(() => 
    activeTab || (tabList[0]?.id || null)
  );

  // Effect optimizado para sincronizar con prop externa
  useEffect(() => {
    if (activeTab) {
      setActiveTabId(activeTab);
    }
  }, [activeTab]);

  /**
   * Handler optimizado para el cambio de tab
   * @param {string} tabId - ID del tab a activar
   */
  const handleTabClick = useCallback((tabId) => {
    setActiveTabId(tabId);
    onTabChange?.(tabId);
  }, [onTabChange]);

  /**
   * Handler optimizado para navegación por teclado
   * @param {KeyboardEvent} e - Evento de teclado
   * @param {string} tabId - ID del tab
   */
  const handleKeyDown = useCallback((e, tabId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTabClick(tabId);
    }
  }, [handleTabClick]);

  /**
   * Genera estilos variantes memoizados para optimizar renders
   * @returns {Object} Objeto con estilos active e inactive
   */
  const variantStyles = useMemo(() => {
    const baseTab = 'tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-transition-all tw-duration-200';
    
    switch (variant) {
      case 'pills':
        return {
          active: `${baseTab} tw-bg-blue-500 tw-text-white tw-rounded-full`,
          inactive: `${baseTab} tw-bg-gray-100 tw-text-gray-600 tw-rounded-full hover:tw-bg-gray-200`
        };
      case 'underline':
        return {
          active: `${baseTab} tw-text-blue-500 tw-border-b-2 tw-border-blue-500 tw-pb-2`,
          inactive: `${baseTab} tw-text-gray-500 tw-border-b-2 tw-border-transparent tw-pb-2 hover:tw-text-gray-700`
        };
      case 'bordered':
        return {
          active: `${baseTab} tw-text-blue-500 tw-border tw-border-blue-500 tw-bg-blue-50 tw-rounded-t-lg`,
          inactive: `${baseTab} tw-text-gray-500 tw-border tw-border-gray-300 tw-bg-white tw-rounded-t-lg hover:tw-bg-gray-50`
        };
      default:
        return {
          active: `${baseTab} tw-text-blue-500 tw-border-b-2 tw-border-blue-500 tw-rounded-t-lg`,
          inactive: `${baseTab} tw-border-b tw-border-transparent tw-rounded-t-lg tw-text-gray-500 hover:tw-text-gray-700`
        };
    }
  }, [variant]);

  /**
   * Genera estilos de tamaño memoizados
   * @returns {string} Clases CSS para el tamaño
   */
  const sizeStyles = useMemo(() => {
    switch (size) {
      case 'sm':
        return 'tw-p-2 tw-text-sm';
      case 'lg':
        return 'tw-p-3 md:tw-p-5 lg:tw-p-6 tw-text-base lg:tw-text-lg';
      default:
        return 'tw-p-2 md:tw-p-3 lg:tw-p-4 tw-text-sm md:tw-text-base';
    }
  }, [size]);

  // Clases memoizadas para tabs
  const tabClassNames = useMemo(() => ({
    active: `${variantStyles.active} ${sizeStyles} ${activeTabClassName}`.trim(),
    inactive: `${variantStyles.inactive} ${sizeStyles} ${inactiveTabClassName}`.trim()
  }), [variantStyles, sizeStyles, activeTabClassName, inactiveTabClassName]);

  // Tab actual memoizado
  const currentTab = useMemo(() => 
    tabList.find((tab) => tab.id === activeTabId),
    [tabList, activeTabId]
  );

  // Clases de contenedor memoizadas
  const containerClassName = useMemo(() => 
    `tw-wrapper tw-w-full ${className}`.trim(),
    [className]
  );
  
  // Clases del contenedor de tabs memoizadas
  const finalTabsContainerClassName = useMemo(() => {
    const defaultTabsContainer = variant === 'pills' 
      ? 'tw-flex tw-gap-1 tw-bg-gray-100 tw-p-1 tw-rounded-full tw-w-fit'
      : 'tw-flex tw-flex-wrap tw--mb-px';
    
    return `${defaultTabsContainer} ${tabsContainerClassName}`.trim();
  }, [variant, tabsContainerClassName]);

  // Clases del contenido memoizadas
  const finalContentClassName = useMemo(() => {
    const defaultContentClassName = variant === 'pills' || variant === 'underline'
      ? 'tw-w-full tw-mt-4'
      : 'tw-w-full tw-mt-4 tw-pl-0 md:tw-pl-4';
    
    return `${defaultContentClassName} ${contentClassName}`.trim();
  }, [variant, contentClassName]);

  // Clases del wrapper de tabs memoizadas
  const finalTabsWrapperClassName = useMemo(() => {
    const baseWrapperStyles = 'tw-text-sm tw-text-center tw-text-gray-500';
    const defaultMargin = 'tw-m-0 md:tw-m-2 lg:tw-m-4';
    
    return tabsWrapperClassName 
      ? `${baseWrapperStyles} ${tabsWrapperClassName}`.trim()
      : `${baseWrapperStyles} ${defaultMargin}`.trim();
  }, [tabsWrapperClassName]);

  if (!currentTab) return null;

  return (
    <div className={containerClassName}>
      <div className={finalTabsWrapperClassName}>
        <ul className={`tw-list-none tw-m-0 tw-pl-0 ${finalTabsContainerClassName}`.trim()}>
          {tabList.map((tab) => (
            <li
              key={tab.id}
              role="presentation"
              className="tw-list-none"
            >
              <button
                type="button"
                className={`tw-appearance-none tw-bg-transparent tw-border-0 focus:tw-outline-none ${tab.id === activeTabId ? tabClassNames.active : tabClassNames.inactive}`.trim()}
                onClick={() => handleTabClick(tab.id)}
                onKeyDown={(e) => handleKeyDown(e, tab.id)}
                role="tab"
                aria-selected={tab.id === activeTabId}
              >
                {tab.icon && <span className="tw-mr-1">{tab.icon}</span>}
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className={finalContentClassName}>
        {keepMounted ? (
          tabList.map((tab) => (
            <div
              key={tab.id}
              role="tabpanel"
              hidden={tab.id !== activeTabId}
              aria-hidden={tab.id !== activeTabId}
            >
              {tab.content || null}
            </div>
          ))
        ) : (
          currentTab.content || null
        )}
      </div>
    </div>
  );
});

// Configurar displayName para debugging
TabsComponentMC.displayName = 'TabsComponentMC';

export default TabsComponentMC;
